/**
 * VotingCard — main voting/polling component.
 * Supports approve-veto, single-choice, ranked, and thumbs voting modes.
 * Manages local vote state and delegates to sub-components for rendering.
 */
import React, { useState, useMemo, useCallback } from 'react';
import { type Component } from '@/types';
import type {
  VoteOption,
  VotingCardProperties,
  VoteResolution,
  Vote,
  VoteResult as VoteResultType,
} from '@/types/pm-types';
import { computeVoteResults, checkResolution } from '@/utils/voteResolution';
import { VoteOptionCard } from './VoteOptionCard';
import { VoteStatus } from './VoteStatus';
import { VoteResultDisplay } from './VoteResult';
import { VoteDeadline } from './VoteDeadline';

// ─── Component Props ────────────────────────────────────────────
interface VotingCardComponentProps {
  component: Component;
  data?: Record<string, unknown>[];
  isViewerMode?: boolean;
}

const DEFAULT_SAMPLE_OPTIONS: VoteOption[] = [
  { id: 'opt-1', label: 'Option A', description: 'First choice for the team' },
  { id: 'opt-2', label: 'Option B', description: 'Alternative approach' },
  { id: 'opt-3', label: 'Option C', description: 'Budget-friendly option' },
];

const DEFAULT_RESOLUTION: VoteResolution = {
  type: 'majority',
  timeoutAction: 'extend',
};

// ─── Main Component ─────────────────────────────────────────────
export const VotingCard: React.FC<VotingCardComponentProps> = ({
  component,
  data,
  isViewerMode = false,
}) => {
  const props = (component.properties || {}) as Partial<VotingCardProperties>;
  const title = props.title || 'Vote';
  const description = props.description;
  const voteType = props.voteType || 'approve-veto';
  const resolution = props.resolution || DEFAULT_RESOLUTION;
  const showResults = props.showResults || 'after-vote';
  const showVoterNames = props.showVoterNames ?? true;
  const deadline = props.deadline;
  const allowChangeVote = props.allowChangeVote ?? true;
  const optionLayout = props.optionLayout || 'list';
  const accentColor = props.accentColor;

  // Resolve options from data source or properties
  const options: VoteOption[] = useMemo(() => {
    if (data && data.length > 0) {
      return data.map((row, i) => ({
        id: String(row.id || row.optionId || `data-opt-${i}`),
        label: String(row.label || row.optionLabel || row.name || `Option ${i + 1}`),
        description: row.description ? String(row.description) : undefined,
        image: row.image ? String(row.image) : undefined,
      }));
    }
    return props.options || DEFAULT_SAMPLE_OPTIONS;
  }, [data, props.options]);

  // Local vote state (standalone mode — no writebackConfig)
  const [votes, setVotes] = useState<Vote[]>([]);
  const [resolvedAt, setResolvedAt] = useState<string | undefined>();

  // Current user (placeholder — in production, comes from auth context)
  const currentUserId = 'current-user';
  const currentUserName = 'You';

  // Derived state
  const results = useMemo(() => computeVoteResults(votes, options), [votes, options]);
  const totalEligible = 6; // placeholder for demo — in production from project members

  const resolutionCheck = useMemo(
    () => checkResolution(results, resolution, totalEligible, deadline),
    [results, resolution, totalEligible, deadline]
  );

  const isResolved = !!resolvedAt || resolutionCheck.resolved;
  const myVotes = useMemo(() => {
    const map: Record<string, Vote> = {};
    votes.filter((v) => v.voterId === currentUserId).forEach((v) => {
      map[v.optionId] = v;
    });
    return map;
  }, [votes]);

  const hasVoted = Object.keys(myVotes).length > 0;
  const isPastDeadline = deadline ? new Date() > new Date(deadline) : false;

  // Auto-resolve when resolution reached
  React.useEffect(() => {
    if (resolutionCheck.resolved && !resolvedAt) {
      setResolvedAt(new Date().toISOString());
    }
  }, [resolutionCheck.resolved, resolvedAt]);

  // ─── Vote Handlers ──────────────────────────────────────────
  const handleVote = useCallback((optionId: string, decision: 'approve' | 'veto' | 'abstain') => {
    if (isViewerMode || isResolved || isPastDeadline) return;

    setVotes((prev) => {
      let next = [...prev];

      if (voteType === 'single-choice') {
        // Remove previous vote from this user (only one selection allowed)
        next = next.filter((v) => v.voterId !== currentUserId);
        if (decision === 'abstain') return next; // toggle off
      } else {
        // Remove existing vote on this option from this user
        const existing = next.findIndex(
          (v) => v.voterId === currentUserId && v.optionId === optionId
        );
        if (existing !== -1) {
          if (!allowChangeVote && decision !== 'abstain') return prev;
          next.splice(existing, 1);
        }
        if (decision === 'abstain') return next; // toggle off
      }

      next.push({
        voterId: currentUserId,
        voterName: currentUserName,
        optionId,
        decision,
        timestamp: new Date().toISOString(),
      });

      return next;
    });
  }, [isViewerMode, isResolved, isPastDeadline, voteType, allowChangeVote]);

  // ─── Layout Styles ──────────────────────────────────────────
  const layoutStyle: React.CSSProperties = useMemo(() => {
    switch (optionLayout) {
      case 'grid':
        return {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 8,
        };
      case 'card':
        return {
          display: 'flex',
          flexDirection: 'column' as const,
          gap: 10,
        };
      case 'list':
      default:
        return {
          display: 'flex',
          flexDirection: 'column' as const,
          gap: 6,
        };
    }
  }, [optionLayout]);

  // ─── Render ─────────────────────────────────────────────────
  return (
    <div
      data-testid="voting-card"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        overflow: 'auto',
        background: 'white',
        borderRadius: 8,
      }}
    >
      {/* Header */}
      <div style={{
        padding: '12px 14px 8px',
        borderBottom: '1px solid #F3F4F6',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 15 }}>&#x1F5F3;&#xFE0F;</span>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1F2937', margin: 0, flex: 1 }}>
            {title}
          </h3>
          {deadline && (
            <VoteDeadline
              deadline={deadline}
              isResolved={isResolved}
              accentColor={accentColor}
            />
          )}
        </div>
        {description && (
          <p style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 0', lineHeight: 1.4 }}>
            {description}
          </p>
        )}
        {/* Vote type badge */}
        <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 3,
            background: '#EEF2FF', color: '#4338CA',
          }}>
            {voteType.replace('-', '/')}
          </span>
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 3,
            background: '#F3F4F6', color: '#6B7280',
          }}>
            {resolution.type}
          </span>
          {isViewerMode && (
            <span style={{
              fontSize: 10, padding: '2px 6px', borderRadius: 3,
              background: '#FEF3C7', color: '#92400E',
            }}>
              View Only
            </span>
          )}
        </div>
      </div>

      {/* Content area */}
      <div style={{ flex: 1, padding: 14, overflow: 'auto' }}>
        {/* Resolved result display */}
        {isResolved && (
          <div style={{ marginBottom: 12 }}>
            <VoteResultDisplay
              results={results}
              options={options}
              resolution={resolution}
              resolvedAt={resolvedAt}
              accentColor={accentColor}
            />
          </div>
        )}

        {/* Options list */}
        {!isResolved && (
          <div style={layoutStyle}>
            {options.map((option, idx) => {
              const optResult = results.find((r) => r.optionId === option.id);
              const optionVoters = votes.filter((v) => v.optionId === option.id);

              return (
                <VoteOptionCard
                  key={option.id}
                  option={option}
                  voteType={voteType}
                  myVote={myVotes[option.id]}
                  result={optResult}
                  showResults={showResults}
                  showVoterNames={showVoterNames}
                  hasVoted={hasVoted}
                  isResolved={isResolved}
                  accentColor={accentColor}
                  disabled={isViewerMode || isPastDeadline}
                  voters={optionVoters}
                  onVote={handleVote}
                  rank={voteType === 'ranked' ? idx + 1 : undefined}
                />
              );
            })}
          </div>
        )}

        {/* Overall status (when not resolved) */}
        {!isResolved && results.length > 0 && hasVoted && (
          <div style={{ marginTop: 12 }}>
            {results.map((r) => (
              <div key={r.optionId} style={{ marginBottom: 6 }}>
                <VoteStatus
                  result={r}
                  totalEligible={totalEligible}
                  resolution={resolution}
                  accentColor={accentColor}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '6px 14px 10px',
        borderTop: '1px solid #F3F4F6',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 10, color: '#9CA3AF' }}>
          {options.length} option{options.length !== 1 ? 's' : ''}
          {hasVoted && ' · You voted'}
        </span>
        <span style={{ fontSize: 10, color: '#9CA3AF' }}>
          {votes.length} vote{votes.length !== 1 ? 's' : ''} cast
        </span>
      </div>
    </div>
  );
};

VotingCard.displayName = 'VotingCard';
